import '../styles/App.scss';
import JwtDecode from 'jwt-decode';
import classnames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { Box, Slide, Paper } from '@mui/material';
import ApiManager from '../libs/ApiManager';
import FormManager from '../libs/FormManager';
import EventManager from '../libs/EventManager';
import { FEATURE_TAGS } from './utils/FeatureTags';
import CircularIndeterminate from './utils/Progress';
import { AuthManager, FirebaseAuthManager } from '@alfred-systems/frontend-shared-components';
import SignInContainer, { PRODUCT_NAME } from './containers/SignInContainer';
import QuestionContainer from './containers/QuestionContainer';
import CategoryContainer from './containers/CategoryContainer';
import SubmitFormContainer from './containers/SubmitFormContainer';
import AnnouncementContext from './context/AnnouncementContext';
import AccountDeletionContainer from './containers/AccountDeletionContainer';
import DoneContainer from './containers/DoneContainer';
import FormContext from './context/FormContext';
import PreviewModalContext from './context/PreviewModalContext';
import PreviewModal from './utils/PreviewModal';
import BasicModal from './utils/BasicModal';
import ErrorMessageContext from './context/ErrorMessageContext';
import FaqContainer from './containers/FaqContainer';
import { getCookie, getModel, getOsVersion, isSystemLogsSupport } from './utils/utils';

const firebaseAuthManager = new FirebaseAuthManager();

const PAGES = {
  ACCOUNT_DELETION: 'account_deletion',
  FEEDBACK_FORM: 'feedback_form',
  DONE: 'done',
}

const ALFRED_AUTH_TOKEN = 'ALFRED_AUTH_TOKEN';

function App() {
  const [email, setEmail] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAnonymousUser, setIsAnonymousUser] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [locale, setLocale] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [platform, setPlatForm] = useState(null);
  const [version, setVersion] = useState(null);
  const [osVersion, setOsVersion] = useState(null);
  const [membership, setMembership] = useState(null);
  const [provider, setProvider] = useState(null);
  const [isSignOutShow, setSignOutShow] = useState(false);
  const [category, setCategory] = useState(null);
  const [searchResult, setSearchResult] = useState(null);
  const [submitFormData, setSubmitFormData] = useState(null);
  const [systemLogs, setSystemLogs] = useState(null);
  const [accountDeletionAuthCode, setAccountDeletionAuthCode] = useState();
  const [common, setCommon] = useState(null);
  const [previewModalData, setPreviewModalData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [form, setForm] = useState(null);
  const [isRestoreModalOpen, setRestoreModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDone, setDone] = useState(false);

  useEffect(() => {
    // Setup system log callback for app
    window.systemLogCallback = function (string) {
      const log = JSON.parse(string);
      if (log.jid) {
        setSystemLogs((prev) => ({
          ...prev,
          [log.jid]: log
        }));
      }
    }
    window.accountDeletionCallback = function (err, code) {
      if (!err) {
        setAccountDeletionAuthCode(code);
      }
    }
  }, []);

  useEffect(() => {
    if (isLoading && locale) {
      setIsLoading(false);
    }
  }, [locale, isLoading]);

  useEffect(() => {
    (async () => {
      try {
        const locale = await ApiManager.getLocale();
        setLocale(locale);
      } catch (err) {
        setErrorMessage(err);
      }
    })()
  }, [])

  useEffect(() => {
    // Cookie was set in cloudfront function
    const cookieToken = getCookie(ALFRED_AUTH_TOKEN);
    const query = new URLSearchParams(window.location.search);
    const token = cookieToken || query.get('token');
    if (token) {
      setToken(token);
      const decodeInfo = JwtDecode(token);
      setUserId(decodeInfo?.unique_id);
      EventManager.setUserProperties(decodeInfo?.unique_id);
      EventManager.enterFeedbackForm();
    }
    setPlatForm(query.get('os') || 'web');
    setMembership(query.get('user') || '');
    setProvider(query.get('provider') || '');
    const ver = Number((query.get('version') || '').replace(/\D+/g, ''));
    if (!isNaN(ver)) {
      setVersion(ver);
    }
    setOsVersion(query.get('os_version') || '');
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (userId && token && query.get('account-deletion')) {
      let id = FormManager.getFormId(userId);
      const onError = sessionErrorHandler();
      (async () => {
        try {
          if (!id) {
            id = await ApiManager.createForm(token);
            FormManager.setFormId(userId, id);
            FormManager.setForm(id, { entry: PAGES.ACCOUNT_DELETION });
          }
        } catch (err) {
          onError(err);
        }
      })();

      setSubmitFormData({
        data: {
          labels: {
            'page': [PAGES.ACCOUNT_DELETION],
          },
        },
        questions: [],
        history: [],
      })
    }

  }, [userId, token]);

  useEffect(() => {
    if (userId) {
      const query = new URLSearchParams(window.location.search);
      const isAccountDeletionEntry = !!query.get('account-deletion');
      const id = FormManager.getFormId(userId);
      if (id) {
        const params = FormManager.getForm(id);
        if (!(isAccountDeletionEntry ^ (params?.entry === PAGES.ACCOUNT_DELETION))) {
          setRestoreModalOpen(true);
        }
      }
    }
  }, [userId]);

  useEffect(() => {
    // ignore a response that arrives after sign-out or a token change, so it can't repopulate
    // the previous user's data
    let stale = false;
    async function getUserSubscription () {
      try {
        const result = await ApiManager.getUserSubscription(token);
        const user = await firebaseAuthManager.getCurrentUser();
        // setUserSubscription re-runs this effect and marks it stale, so finish both reads first
        if (stale) return;
        setUserSubscription(result);
        if (!user) {
          const decodeInfo = JwtDecode(token);
          EventManager.setUserProperties(decodeInfo?.unique_id, result !== 'premium');
        }
      } catch (err) {
        if (!stale) setErrorMessage(err);
      }
    }
    if (token && !userSubscription) {
      getUserSubscription();
    }
    return () => {
      stale = true;
    };
  }, [token, userSubscription]);

  useEffect(() => {
    let stale = false;
    async function getUserData () {
      try {
        const data = await ApiManager.getUser(token);
        if (stale) return;
        setEmail(data.display_email);
        setIsAnonymousUser(data.is_anonymous);
      } catch (err) {
        if (!stale) setErrorMessage(err);
      }
    }
    if (token && !email) {
      getUserData();
    }
    return () => {
      stale = true;
    };
  }, [token, email]);

  // Firebase user -> null covers our own sign-out and a sign-out from another app sharing the
  // same Firebase storage (e.g. WebViewer). Token-only entries (cookie / query) never have a
  // Firebase user, so only a transition from signed in to signed out resets the state.
  const hasFirebaseUser = useRef(false);
  // bumped on every sign-out, so per-user requests started before it can drop their results
  const signOutCount = useRef(0);
  // used as a key to remount the containers, so their local state (form values, uploads,
  // account deletion step, ...) doesn't carry over to the next account
  const [session, setSession] = useState(0);
  // call at the start of a per-user async operation; the returned handler drops errors that
  // land after a sign-out, so they don't pop up over the sign-in screen or the next account
  const sessionErrorHandler = () => {
    const count = signOutCount.current;
    return (err) => {
      if (count === signOutCount.current) setErrorMessage(err);
    };
  };
  useEffect(() => {
    let active = true;
    const unsubscribe = firebaseAuthManager.auth.onAuthStateChanged(async (user) => {
      if (!active) return;
      setSignOutShow(!!(user?.emailVerified && user?.refreshToken));
      if (user) {
        hasFirebaseUser.current = true;
        return;
      }
      if (!hasFirebaseUser.current) return;
      hasFirebaseUser.current = false;
      signOutCount.current += 1;
      setSession(signOutCount.current);
      // reset per-user state so the next account doesn't inherit it (e.g. the !userSubscription guard)
      setToken(null);
      setEmail(null);
      setUserId(null);
      setIsAnonymousUser(false);
      setUserSubscription(null);
      setCategory(null);
      setSearchResult(null);
      setSubmitFormData(null);
      setForm(null);
      setDone(false);
      setRestoreModalOpen(false);
      setSystemLogs(null);
      setAccountDeletionAuthCode(undefined);
      setPreviewModalData(null);
      setErrorMessage(null);
      try {
        // clears ALFRED_FEEDBACK_FORM_SETTINGS (kvToken / refresh token) and the analytics user
        await AuthManager.signOut(PRODUCT_NAME);
      } catch (err) {
        if (active) setErrorMessage(err);
      }
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  function onSignInComplete (email, token, uid) {
    setEmail(email);
    setToken(token);
    setUserId(uid);
    EventManager.enterFeedbackForm();
  }

  function onQuestionComplete (data, history = null, questions = []) {
    if (isSystemLogsSupport(platform, version) && !systemLogs) {
      setSystemLogs({});
      window.location.href = 'alfred-action://feedback-form#system-log';
    }
    const newQuestions = [
      ...common,
      ...questions
    ];
    if (isAnonymousUser) {
      const id = `feature:contact_email_for_anonymous_user`;
      data.labels['feature'].push(id);
      newQuestions.push({
        id,
        name: t('form.contact_email'),
        answers: [{
          id: `${id}_email`,
          title: 'example@example.com',
          body: t('form.contact_email_desc'),
          labels: {
            feature: ['email'],
            email: ['required']
          },
        }],
      });
    }
    setSubmitFormData({
      data,
      questions: newQuestions,
      history
    });
  }

  const onCategoriesLoad = async (newCategories, commonCategories) => {
    try {
      let data = [];
      for (const category of commonCategories) {
        const result = await ApiManager.listQuestions(category.id, locale);
        setAnnouncements(result.filter(r => r.description === FEATURE_TAGS.question.announcement));
        data =  [...data, ...result];
      }
      setCommon(data);
    } catch (err) {
      setErrorMessage(err);
    }
  }

  async function signOut () {
    // state and local auth data are cleared by the onAuthStateChanged listener
    try {
      await firebaseAuthManager.signOut();
    } catch (err) {
      setErrorMessage(err);
    }
  }

  const getSubmitFormStatus = () => {
    const status = {}
    if (submitFormData) {
      const pages = submitFormData.data?.labels['page'];
      pages?.length > 0 ? Object.assign(status, { [pages[0]]: true })
        : Object.assign(status, { [PAGES.FEEDBACK_FORM] : true });
    }
    if (isDone) {
      Object.assign(status, { [PAGES.DONE]: true });
    }
    return status;
  }

  const onFormRestore = async () => {
    const count = signOutCount.current;
    try {
      setRestoreModalOpen(false);
      const id = FormManager.getFormId(userId);
      if (id && token) {
        const data = await ApiManager.getForm(id, token);
        if (count !== signOutCount.current) return;
        if (data?.category) {
          setCategory(data?.category);
        }
        setForm(data);
      }
    } catch (err) {
      if (count === signOutCount.current) setErrorMessage(err);
    }
  }

  const onFormDiscard = async () => {
    if (userId) {
      const id = FormManager.getFormId(userId);
      FormManager.delFormData(userId);
      if (id && token) {
        const onError = sessionErrorHandler();
        try {
          await ApiManager.discardForm(id, token);
        } catch (err) {
          onError(err);
        }
      }
    }
    setForm(null);
    setRestoreModalOpen(false);
  }

  const onCategoryChange = async (category) => {
    setCategory(category);
    if (category) {
      EventManager.clickCategory(category.id);
      let id = FormManager.getFormId(userId);
      const onError = sessionErrorHandler();
      try {
        if (!id) {
          id = await ApiManager.createForm(token);
          FormManager.setFormId(userId, id);
        }
        await ApiManager.saveForm(id, { category, history: [], values: {} }, token);
      } catch (err) {
        onError(err);
      }
    } else {
      await onFormDiscard();
    }
  }

  const onFormChange = async (data) => {
    const id = FormManager.getFormId(userId);
    if (id) {
      const onError = sessionErrorHandler();
      try {
        await ApiManager.saveForm(id, data, token);
      } catch (err) {
        onError(err);
      }
    }
  }

  const onFormSubmit = async (values, data, tags, contactEmail) => {
    const count = signOutCount.current;
    let ticketId = null;
    try {
      const id = FormManager.getFormId(userId);
      if (submitFormData && id) {
        const { history } = submitFormData;
        let model = null;
        if (platform !== 'web' && token) {
          const decoded = JwtDecode(token);
          if (decoded.jid) model = decoded.jid.replace(/.*@.*\/ivuu\d{2}(.*).{12}/, '$1');
        }
        if (!model) model = getModel(window.navigator.userAgent);
        ticketId = await ApiManager.submitForm(id, {
          category,
          history,
          values,
          data,
          tags,
          contact_email: contactEmail,
          viewer: {
            membership: membership || userSubscription || 'free',
            device_language: window.navigator.language || window.navigator.userLanguage,
            model,
            app_version: [platform, version].filter(Boolean).join(' '),
            os_version: !platform || platform === 'web' ? getOsVersion(window.navigator.userAgent)
              : [platform, osVersion].filter(Boolean).join(' '),
          },
        }, token);
        FormManager.delFormData(userId);
        if (count === signOutCount.current) setDone(true);
      }
    } catch (err) {
      if (count === signOutCount.current) setErrorMessage(err);
    }
    return ticketId;
  }

  const onClose = () => {
    if (platform === 'web') {
      window.location.reload();
    } else {
      window.location.href = 'alfred-action://close';
    }
  }

  const onErrorMessageClose = () => {
    if (errorMessage?.response?.status === 403) {
      onClose();
    } else {
      setErrorMessage(null);
    }
  }

  const handleErrorMessage = () => {
    if (errorMessage?.response?.status === 403) {
      return t('dialog.submit_fail.text');
    }
    return errorMessage?.message || errorMessage;
  }

  const onAccountDeletionClose = async () => {
    const query = new URLSearchParams(window.location.search);
    if (query.get('account-deletion')) {
      await onFormDiscard();
      onClose();
    } else {
      setSubmitFormData(null);
    }
  }

  const show = getSubmitFormStatus();

  return (<ErrorMessageContext.Provider value={{ error: errorMessage, onError: setErrorMessage }}>
    <FormContext.Provider value={{ email, form, token, onChange: onFormChange, onDiscard: onFormDiscard }}>
      <PreviewModalContext.Provider value={{ url: previewModalData?.url, type: previewModalData?.type, open: (url, type) => setPreviewModalData({ url, type }) }}>
        <AnnouncementContext.Provider value={{ announcements: announcements }}>
          <div className={classnames('app', platform, version)}>
            <div className='body'>
              <Box key={session} sx={{ height: '100%', position: 'relative' }}>
                <CircularIndeterminate show={isLoading} />
                <SignInContainer onComplete={onSignInComplete} show={!token} />
                <CategoryContainer
                  locale={locale}
                  onLoad={onCategoriesLoad}
                  onSearchClick={setSearchResult}
                  onSignOut={isSignOutShow ? signOut : null}
                  onClick={onCategoryChange}
                  onClose={onClose}
                />
                <Slide direction='left' in={!!searchResult}>
                  <Paper className='container' elevation={0}>
                    <FaqContainer
                      faq={searchResult}
                      locale={locale}
                      onSignOut={isSignOutShow ? signOut : null}
                      onSubmit={onQuestionComplete}
                      onClose={() => setSearchResult(null)}
                    />
                  </Paper>
                </Slide>
                <Slide direction='left' in={!!category}>
                  <Paper className='container' elevation={0}>
                    <QuestionContainer
                      category={category}
                      onSignOut={isSignOutShow ? signOut : null}
                      onClose={() => onCategoryChange(null)}
                      onComplete={onQuestionComplete}
                      locale={locale}
                    />
                  </Paper>
                </Slide>
                <Slide direction='left' in={show[PAGES.ACCOUNT_DELETION]}>
                  <Paper className='container' elevation={0}>
                    <AccountDeletionContainer
                      email={email}
                      token={token}
                      authCode={accountDeletionAuthCode}
                      version={version}
                      platform={platform}
                      provider={provider}
                      onClose={onAccountDeletionClose}
                      isPremium={userSubscription === 'premium'}
                    />
                  </Paper>
                </Slide>
                <Slide direction='left' in={show[PAGES.FEEDBACK_FORM]}>
                  <Paper className='container' elevation={0}>
                    <SubmitFormContainer
                      token={token}
                      version={version}
                      platform={platform}
                      questions={submitFormData?.questions || []}
                      data={submitFormData?.data}
                      systemLogs={systemLogs}
                      onClose={() => setSubmitFormData(null)}
                      onSubmit={onFormSubmit}
                    />
                  </Paper>
                </Slide>
                <Slide direction='left' in={show[PAGES.DONE]}>
                  <Paper className='container' elevation={0}>
                    <DoneContainer onClose={onClose} />
                  </Paper>
                </Slide>
              </Box>
            </div>
            <BasicModal
              open={isRestoreModalOpen && !!token}
              description={t('dialog.clear_cache.text')}
              confrimText={t('dialog.yes')}
              onConfirm={onFormRestore}
              cancelText={t('dialog.no')}
              onCancel={onFormDiscard}
              onClose={onFormDiscard}
            />
            <BasicModal
              open={!!errorMessage}
              description={handleErrorMessage()}
              cancelText={t('dialog.ok')}
              onCancel={onErrorMessageClose}
              onClose={onErrorMessageClose}
            />
            <PreviewModal url={previewModalData?.url} type={previewModalData?.type} onClose={() => setPreviewModalData(null)} />
          </div>
        </AnnouncementContext.Provider> 
      </PreviewModalContext.Provider>
    </FormContext.Provider>
  </ErrorMessageContext.Provider>);
}

export default App;
